import { UserProfile, UserSecurity, UserSessions } from '@workos-inc/widgets';
import { useOutletContext } from 'react-router-dom';

import { makeAuthTokenGetter } from '../lib/widget-token.ts';

import type { SettingsOutletContext } from './Settings.tsx';

/**
 * /settings/profile — three WorkOS user-self widgets stacked.
 *
 * Each widget owns its own auth token; `makeAuthTokenGetter` returns a
 * fresh fetcher per widget so token expiry is handled by the widget
 * internally (per docs: invokes the function when its token expires).
 *
 * Each section gets a subtitle. A widget may legitimately render empty
 * (e.g. UserSecurity has nothing to show for users signed in only via
 * Google OAuth — no password / MFA / additional connections to manage).
 * The subtitle keeps the section from looking broken in those cases.
 */
export function SettingsProfile() {
  const { orgSlug } = useOutletContext<SettingsOutletContext>();

  return (
    <div className="stack" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <section>
        <h2 style={{ marginBottom: '0.25rem' }}>Profile</h2>
        <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          Display name, email, and avatar from your auth provider.
        </p>
        <UserProfile authToken={makeAuthTokenGetter(orgSlug, 'user-profile')} />
      </section>
      <section>
        <h2 style={{ marginBottom: '0.25rem' }}>Security</h2>
        <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          Password and multi-factor authentication. Nothing appears here if
          you sign in only via a social provider (Google, etc.) and haven&apos;t
          set up additional factors.
        </p>
        <UserSecurity authToken={makeAuthTokenGetter(orgSlug, 'user-security')} />
      </section>
      <section>
        <h2 style={{ marginBottom: '0.25rem' }}>Active sessions</h2>
        <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          Sessions across your devices and browsers. Sign out of any you
          don&apos;t recognize.
        </p>
        <UserSessions authToken={makeAuthTokenGetter(orgSlug, 'user-sessions')} />
      </section>
    </div>
  );
}
